import { ChevronDownIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import { debounce } from 'lodash';
import React, { useCallback, useState } from "react";
import { useController } from "react-hook-form";
import { components } from 'react-select';
import AsyncSelect from 'react-select/async';

import BaseInput from "./BaseInput";
import BaseInputProps from "./BaseInputProps";

const DropdownIndicator = (props: any) => {
  return (
    <components.DropdownIndicator {...props}>
      <ChevronDownIcon />
    </components.DropdownIndicator>
  );
};

const controlStyles = {
  base: 'mt-2 block w-full flex rounded-md border-0 pl-1 mr-3 text-limed-spruce-800 ring-1 ring-inset ring-porcelain-300 focus:ring-2 focus:ring-bondi-blue-600 text-base sm:leading-6',
  focus: 'border-primary-600 ring-1 ring-primary-500',
  nonFocus: 'border-gray-300 hover:border-gray-400',
};
const placeholderStyles = 'text-limed-spruce-800 pl-1 py-0.5';
const selectInputStyles = 'pl-1 py-0.5';
const valueContainerStyles = 'p-1 gap-1';
const singleValueStyles = 'leading-7 ml-1';
const indicatorsContainerStyles = 'flex p-1 gap-1';
const indicatorSeparatorStyles = 'bg-gray-300';
const dropdownIndicatorStyles = 'p-1 hover:bg-gray-100 text-limed-spruce-800 w-6 h-6 rounded-md hover:text-black';
const menuStyles = 'p-1 mt-2 border border-gray-200 bg-white rounded-lg';
const groupHeadingStyles = 'ml-3 mt-2 mb-1 text-limed-spruce-800 text-sm';
const optionStyles = {
  base: 'hover:cursor-pointer px-3 py-2 rounded',
  focus: 'bg-gray-100 active:bg-gray-200',
  selected: "text-bondi-blue-600",
};
const noOptionsMessageStyles = 'text-mid-gray-500 p-2 bg-gray-50 border border-dashed border-gray-200 rounded-sm';

interface Option {
  value: string;
  label: string;
}

export interface AsyncSelectInputProps<T = Option> extends BaseInputProps {
  defaultOptions?: T[];
  placeholder?: string;
  fetchOptions: (inputValue: string) => Promise<T[]>;
  fetchDebounce?: number;
}

export default function AsyncSelectInput({ 
  fetchOptions, 
  fetchDebounce = 100, 
  defaultOptions = [], 
  placeholder = 'Select...', 
  ...baseProps
}: AsyncSelectInputProps) {
  const { name } = baseProps;
  const { 
    field: { onChange, value: t } 
  } = useController({ name });
  const [isFocused, setIsFocused] = useState(false);
  const [value, setValue] = useState<Option | null>(null);

  const loadOptions = useCallback(debounce(fetchOptions, fetchDebounce), [fetchOptions]);

  return (
    <BaseInput {...{ isFocused, ...baseProps }}>
      <AsyncSelect<Option>
        placeholder={placeholder}
        onChange={(val) => {
          setValue(val);
          onChange(val?.value);
        }}
        value={value}
        cacheOptions 
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        loadOptions={loadOptions} 
        defaultOptions={defaultOptions}
        components={{ DropdownIndicator }}
        unstyled
        styles={{
          input: base => ({
            ...base,
            'input:focus': {
              boxShadow: 'none',
            },
          }),
          control: base => ({
            ...base,
            minHeight: 34,
            transition: 'none',
          }),
        }}
        classNames={{
          control: ({ isFocused }) => clsx(isFocused ? controlStyles.focus : controlStyles.nonFocus, controlStyles.base),
          placeholder: () => placeholderStyles,
          input: () => selectInputStyles,
          valueContainer: () => valueContainerStyles,
          singleValue: () => singleValueStyles,
          indicatorsContainer: () => indicatorsContainerStyles,
          indicatorSeparator: () => indicatorSeparatorStyles,
          dropdownIndicator: () => dropdownIndicatorStyles,
          menu: () => menuStyles,
          groupHeading: () => groupHeadingStyles,
          option: ({ isFocused, isSelected }) =>
            clsx(isFocused && optionStyles.focus, isSelected && optionStyles.selected, optionStyles.base),
          noOptionsMessage: () => noOptionsMessageStyles,
        }}
      />
    </BaseInput>
  );
}

